// Janelas de recipiente (mochila, bolsas), depot, menu de contexto e escolha de quantidade.
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { DEPOT_SIZE } from '../../../../shared/src/game/items';
import { catalogNow, itemImage } from '../items';
import { ItemImg, ItemSlot, type DragApi } from './ItemSlot';

const SKIN = '/assets/interface/images/skin/classic';

function ContainerIcon(props: { typeId: number }) {
  const [src, setSrc] = useState('');
  useEffect(() => { itemImage(props.typeId).then(setSrc); }, [props.typeId]);
  return src ? <img src={src} width={12} height={12} alt="" /> : null;
}

/** Janela de um recipiente na barra lateral, com os slots no estilo do cliente. */
export function ContainerWidget(props: { box: ItemInstance; items: ItemInstance[]; api: DragApi; onClose: () => void }) {
  const [min, setMin] = useState(false);
  const t = catalogNow()?.get(props.box.typeId);
  const inside = props.items.filter((i) => i.parentId === props.box.id);
  // sem limite de espaço: mostra o volume original ou o que estiver ocupado, sempre com uma fileira livre
  const used = inside.reduce((m, i) => Math.max(m, Number(i.slot) + 1), 0);
  const size = Math.max(t?.container ?? 8, Math.ceil((used + 1) / 4) * 4 + 4);
  const at = (n: number) => inside.find((i) => i.slot === String(n));
  const name = t ? t.name.charAt(0).toUpperCase() + t.name.slice(1) : 'Recipiente';
  return (
    <section class={'w-widget' + (min ? ' min' : '')}>
      <header class="w-title">
        <ContainerIcon typeId={props.box.typeId} />
        <span>{name}</span>
        <span class="w-title-btns">
          <button title={min ? 'Expandir' : 'Minimizar'} onClick={() => setMin(!min)} style={{ backgroundImage: `url(${SKIN}/${min ? 'max' : 'min'}-button-small-up.png)` }} />
          <button title="Fechar" onClick={props.onClose} style={{ backgroundImage: `url(${SKIN}/exit-button-small-up.png)` }} />
        </span>
      </header>
      {!min && (
        <div class="w-body">
          <div class="cont-grid">
            {Array.from({ length: size }, (_, n) => (
              <ItemSlot key={n} item={at(n)} target={{ place: 'container', parentId: props.box.id, slot: n }} api={props.api} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** Supply Pouch: poções e runas (só saem gastas ou destruídas; o peso conta na capacidade). */
export function PouchWidget(props: { items: ItemInstance[]; api: DragApi; onClose: () => void }) {
  const [min, setMin] = useState(false);
  const inside = props.items.filter((i) => i.place === 'pouch');
  const used = inside.reduce((m, i) => Math.max(m, Number(i.slot) + 1), 0);
  const size = Math.max(8, Math.ceil((used + 1) / 4) * 4);
  const at = (n: number) => inside.find((i) => i.slot === String(n));
  const cat = catalogNow();
  const weight = inside.reduce((w, i) => w + (cat?.get(i.typeId)?.weight ?? 0) * i.count, 0);
  return (
    <section class={'w-widget pouch-widget' + (min ? ' min' : '')}>
      <header class="w-title">
        <i class="pouch-ico" />
        <span>Supply Pouch</span>
        <span class="w-title-btns">
          <button title={min ? 'Expandir' : 'Minimizar'} onClick={() => setMin(!min)} style={{ backgroundImage: `url(${SKIN}/${min ? 'max' : 'min'}-button-small-up.png)` }} />
          <button title="Fechar" onClick={props.onClose} style={{ backgroundImage: `url(${SKIN}/exit-button-small-up.png)` }} />
        </span>
      </header>
      {!min && (
        <div class="w-body">
          <div class="cont-grid">
            {Array.from({ length: size }, (_, n) => (
              <ItemSlot key={n} item={at(n)} target={{ place: 'pouch', slot: n }} api={props.api} />
            ))}
          </div>
          <div class="pouch-foot small muted" title="Poções e runas ficam aqui. Só saem quando são usadas ou guardadas no depot.">Não conta na capacidade</div>
        </div>
      )}
    </section>
  );
}

/** Conteúdo do depot (abre numa janela por cima do jogo). */
export function DepotView(props: { items: ItemInstance[]; api: DragApi }) {
  const depot = props.items.filter((i) => i.place === 'depot');
  const used = depot.length;
  const shown = Math.min(DEPOT_SIZE, Math.max(30, Math.ceil((used + 10) / 10) * 10));
  const at = (n: number) => depot.find((i) => i.slot === String(n));
  return (
    <div class="depot">
      <p class="muted small">Arraste itens entre o depot e a sua mochila. Ocupado: {used} de {DEPOT_SIZE}.</p>
      <div class="depot-grid">
        {Array.from({ length: shown }, (_, n) => <ItemSlot key={n} item={at(n)} target={{ place: 'depot', slot: n }} api={props.api} />)}
      </div>
    </div>
  );
}

export function ContextMenu(props: { x: number; y: number; options: Array<[string, () => void]>; onClose: () => void }) {
  // perto da borda da tela o menu abre para cima/para a esquerda, para não ficar cortado
  const ref = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState({ x: props.x, y: props.y });
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect(), W = window.innerWidth, H = window.innerHeight;
    const x = props.x + r.width > W - 4 ? Math.max(4, props.x - r.width) : props.x;
    const y = props.y + r.height > H - 4 ? Math.max(4, props.y - r.height) : props.y;
    if (x !== pos.x || y !== pos.y) setPos({ x, y });
  }, [props.x, props.y]);
  useEffect(() => {
    const close = () => props.onClose();
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close, { capture: true, once: true });
    return () => window.removeEventListener('click', close);
  }, []);
  return (
    <ul ref={ref} class="ctx-menu" style={{ left: pos.x + 'px', top: pos.y + 'px' }} onContextMenu={(e) => e.preventDefault()}>
      {props.options.map(([label, fn]) => <li key={label} onClick={(e) => { e.stopPropagation(); fn(); props.onClose(); }}>{label}</li>)}
    </ul>
  );
}

/** Escolha de quantidade ao mover parte de uma pilha (Ctrl + arrastar), como no cliente. */
export function CountDialog(props: { max: number; typeId: number; onOk: (n: number) => void; onCancel: () => void }) {
  const [n, setN] = useState(props.max);
  return (
    <div class="count-dialog">
      <div class="row"><span class="item-slot"><ItemImg typeId={props.typeId} count={n} /></span><span>Quantos mover?</span></div>
      <input type="range" min={1} max={props.max} value={n} onInput={(e) => setN(Number((e.target as HTMLInputElement).value))} />
      <div class="row between">
        <input class="count-num" type="number" min={1} max={props.max} value={n} onInput={(e) => setN(Math.max(1, Math.min(props.max, Number((e.target as HTMLInputElement).value) || 1)))} />
        <div class="row">
          <button class="tb-button" onClick={props.onCancel}>Cancelar</button>
          <button class="tb-button green" onClick={() => props.onOk(n)}>Ok</button>
        </div>
      </div>
    </div>
  );
}
